
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface LegalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  type: "privacy" | "terms";
}

export const LegalDialog = ({
  isOpen,
  onClose,
  type
}: LegalDialogProps) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-4xl max-h-[80vh]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {type === "privacy" ? "Privacy Policy" : "Terms and Conditions"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Effective Date: June 23, 2025
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <ScrollArea className="h-[60vh] mt-4 pr-4">
          {type === "privacy" ? (
            <div className="space-y-4 text-sm">
              <p><strong>Effective Date: June 23, 2025</strong></p>
              
              <p>At CMPGFB LLC, we respect and protect your privacy. This Privacy Policy explains how your data is collected, used, and safeguarded when using CSO Agent.</p>
              
              <div>
                <h3 className="font-semibold mb-2">1. Data We Collect</h3>
                <p className="mb-2">1.1 Personal Information:</p>
                <ul className="list-disc pl-6 mb-2">
                  <li>Name</li>
                  <li>Company name</li>
                  <li>Email address</li>
                  <li>Payment information via Stripe</li>
                  <li>Wallet address (optional for USDC payouts)</li>
                </ul>
                <p className="mb-2">1.2 Usage Data:</p>
                <ul className="list-disc pl-6 mb-2">
                  <li>Interaction history, referral activity, document generation logs</li>
                </ul>
                <p className="mb-2">1.3 Referral Participation Data:</p>
                <ul className="list-disc pl-6">
                  <li>Payout method selection (USDC wallet or PayPal)</li>
                  <li>Referral link clicks, conversion stats</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">2. How We Use Your Data</h3>
                <p>2.1 To provide and improve the CSO Agent platform and its services.</p>
                <p>2.2 To process payments and subscriptions.</p>
                <p>2.3 To manage the referral and affiliate program.</p>
                <p>2.4 To train AI systems (anonymously) for up to 12 months, after which the data is deleted.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">3. Data Protection</h3>
                <p>3.1 Your data is encrypted and secured using industry-standard technologies.</p>
                <p>3.2 Payment information is handled exclusively by Stripe and is not stored by CSO Agent.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">4. Information Sharing</h3>
                <p className="mb-2">4.1 We do not sell or share your data with third parties except:</p>
                <ul className="list-disc pl-6">
                  <li>Payment processors (Stripe)</li>
                  <li>Law enforcement if required</li>
                  <li>Affiliate payout providers (only for payments you opt into)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">5. Your Rights</h3>
                <p>5.1 You may request to access, update, or delete your personal information at any time.</p>
                <p>5.2 You may unsubscribe from promotional emails or account communications.</p>
                <p>5.3 For deletion requests, contact hello@csoagent.com and allow up to 14 days for processing.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">6. Changes to This Policy</h3>
                <p>We reserve the right to modify this Privacy Policy at any time. Updates will be announced via email or within the CSO Agent dashboard.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">7. Contact Us</h3>
                <p>CMPGFB LLC</p>
                <p>8 The Green, Suite E</p>
                <p>Dover, DE 19901</p>
                <p>Email: hello@csoagent.com</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <p><strong>Effective Date: June 23, 2025</strong></p>
              
              <p>Welcome to CSO Agent, a service provided by CMPGFB LLC, a Delaware company based in the United States. By accessing or using CSO Agent, you agree to be bound by these Terms and Conditions. If you do not agree, you must not use the platform.</p>

              <div>
                <h3 className="font-semibold mb-2">1. Definitions</h3>
                <p>1.1 CSO Agent: Refers to the platform, website, web app, mobile app, and associated services provided by CMPGFB LLC.</p>
                <p>1.2 User: Refers to any individual or entity that accesses or uses CSO Agent.</p>
                <p>1.3 Company: Refers to CMPGFB LLC, the entity that owns and operates CSO Agent.</p>
                <p>1.4 Referral Partner: A registered user who participates in the CSO Agent referral program.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">2. Eligibility</h3>
                <p>2.1 By using the platform, you confirm that you are at least 18 years old and have the legal capacity to enter into this agreement.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">3. User Obligations</h3>
                <p>3.1 You agree to provide accurate, complete, and up-to-date information when creating an account or using the platform.</p>
                <p>3.2 You are responsible for maintaining the confidentiality of your account credentials and are fully responsible for all activities that occur under your account.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">4. Subscription Plans and Payment</h3>
                <p className="mb-2">4.1 Plan Options:</p>
                <ul className="list-disc pl-6 mb-2">
                  <li>Monthly Plan: $19.99/month</li>
                  <li>Team Plan: $99/month</li>
                  <li>Enterprise Plan: $399/month</li>
                  <li>Lifetime Plan: One-time payment of $999 (limited to 999 users)</li>
                </ul>
                <p className="mb-2">4.2 Referral Payouts:</p>
                <p className="mb-2">Referral Partners may receive the following flat-rate commissions per successful paid referral:</p>
                <ul className="list-disc pl-6 mb-2">
                  <li>$5 for Monthly Plan</li>
                  <li>$20 for Team Plan</li>
                  <li>$80 for Enterprise Plan</li>
                  <li>$175 for Lifetime Plan</li>
                </ul>
                <p>4.3 Referral Payout Method: Referral commissions are distributed monthly and paid via USDC (on Base) or PayPal, based on the payout method selected by the Referral Partner. All referral activity is subject to review, fraud protection, and a holding period of up to 30 days to confirm successful payments.</p>
                <p>4.4 Bonus Incentives: The top monthly referrer is eligible to receive a bonus of either $1,000 in USDC (on Base) or $750 via PayPal. Monthly bonus structures and rules may be modified at the discretion of CMPGFB LLC.</p>
                <p>4.5 Payment Processing: All subscription payments are securely processed via Stripe. Users are responsible for ensuring valid and up-to-date payment information.</p>
                <p>4.6 Refund Policy: Due to the nature of CSO Agent as a digital service delivering instant strategic value, all purchases are final. No refunds will be issued once a subscription or payment has been processed. Users are encouraged to evaluate the platform through available trials or demo materials prior to purchase.</p>
                <p>4.7 Subscription Renewal: Monthly subscriptions will renew automatically unless canceled prior to the end of the billing cycle. The Lifetime Plan is a one-time payment and does not renew.</p>
                <p>4.8 Data Deletion Requests: Users may suspend their subscription and request the deletion of their account data. Processing of such requests may take up to 14 business days.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">5. Data Ownership and Usage</h3>
                <p>5.1 Users retain full ownership of all content, strategic insights, and data entered into CSO Agent.</p>
                <p>5.2 User-provided data may be used anonymously to train and improve the CSO Agent AI system for a maximum of one year, after which it is permanently deleted.</p>
                <p>5.3 CMPGFB LLC does not claim ownership of any user-submitted data or materials.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">6. Intellectual Property</h3>
                <p>6.1 All content, software, design assets, branding, and platform technology remain the intellectual property of CMPGFB LLC.</p>
                <p>6.2 Users are granted a non-exclusive, non-transferable license to use the platform for personal, commercial, or organizational purposes in accordance with the terms herein.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">7. Referral Program Compliance</h3>
                <p>7.1 CMPGFB LLC reserves the right to withhold or cancel referral payouts in cases of suspected fraud, self-referrals, or abuse of the referral system.</p>
                <p>7.2 Participation in the referral program requires compliance with all anti-spam, data protection, and fair marketing practices.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">8. Termination</h3>
                <p>8.1 CMPGFB LLC may suspend or terminate user accounts for any breach of these Terms.</p>
                <p>8.2 Users may cancel subscriptions at any time and will retain access until the end of the current billing period.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">9. Limitation of Liability</h3>
                <p>9.1 CSO Agent is provided on an "as-is" basis without warranties of any kind.</p>
                <p>9.2 CMPGFB LLC is not liable for any indirect, incidental, or consequential damages related to your use of the platform.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">10. Governing Law</h3>
                <p>These Terms are governed by the laws of the State of Delaware, USA.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">11. Contact Information</h3>
                <p>For questions about these Terms or the platform:</p>
                <p className="mt-2">CMPGFB LLC</p>
                <p>8 The Green, Suite E</p>
                <p>Dover, DE 19901</p>
                <p>Email: hello@csoagent.com</p>
              </div>
            </div>
          )}
        </ScrollArea>

        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>Close</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
